import { Flex } from '@byecode/ui'
import type { DragEndEvent, DragMoveEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { ActorEvent, BlockAbstract, CustomViewConfig, FieldADTValue, RichTextContentProtocol } from '@lighthouse/core'
import { DataSourceType, DIRECTION, type RecordLikeProtocol } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import {
    ContainerLayoutProvider,
    getPrimaryDsSystemFieldId,
    getRecordPoolKey,
    useAtomData,
    useContainerLayoutContext,
    useFlowLayoutContext,
    ViewEmptyState
} from '@lighthouse/shared'
import { type BreakPointSize,mergeRefs } from '@lighthouse/tools'
import { useDeepCompareMemo } from '@react-hookz/web'
import { AnimatePresence, LayoutGroup } from 'motion/react'
import React, { forwardRef, memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { useStableScrollElement } from '../CustomViewBlock/utils'
import { binarySearchInsertIndex, calculateNewSortValue, getEffectiveSortValue } from './sortUtils'
import type { TreeRecord } from './TreeItem'
import { TreeItem } from './TreeItem'

function buildRecordTree(records: RecordLikeProtocol[], idFieldId: string, parentFieldId: string, sortFieldId?: string): TreeRecord[] {
    // 构建 id -> record 映射表，初始化children数组
    const recordMap = new Map<string, TreeRecord>()
    for (const record of records) {
        const idValue = record.content[idFieldId]?.value?.toString()
        if (idValue) {
            recordMap.set(idValue, { record, children: [], index: 0 })
        }
    }

    /**
     * 二分插入节点到已排序数组
     * 排序规则：按有效排序值升序（sortId 或 -创建时间）
     */
    const insertSorted = (nodes: TreeRecord[], node: TreeRecord): void => {
        if (!sortFieldId) {
            nodes.push(node)
            return
        }
        const effectiveValue = getEffectiveSortValue(node.record, sortFieldId)
        const insertIndex = binarySearchInsertIndex(nodes, effectiveValue, sortFieldId)
        nodes.splice(insertIndex, 0, node)
    }

    // 构建树形结构，使用二分插入排序
    const roots: TreeRecord[] = []
    for (const record of records) {
        const idValue = record.content[idFieldId]?.value?.toString()
        const parentValue = record.content[parentFieldId]?.value?.toString()
        const node = idValue ? recordMap.get(idValue) : undefined

        if (!node) {
            continue
        }

        if (parentValue) {
            const parent = recordMap.get(parentValue)
            if (parent) {
                insertSorted(parent.children, node)
            } else {
                // 父节点不存在，视为根节点
                insertSorted(roots, node)
            }
        } else {
            // 无父节点，这是根节点
            insertSorted(roots, node)
        }
    }

    // 深度优先遍历分配索引
    let currentIndex = 0
    function assignIndex(node: TreeRecord): void {
        node.index = currentIndex++
        for (const child of node.children) {
            assignIndex(child)
        }
    }

    for (const root of roots) {
        assignIndex(root)
    }

    return roots
}

function getAllParentNodeIds(tree: TreeRecord[]): string[] {
    const parentIds: string[] = []

    function traverse(node: TreeRecord): void {
        if (node.children.length > 0) {
            parentIds.push(node.record.id)
            for (const child of node.children) {
                traverse(child)
            }
        }
    }

    for (const root of tree) {
        traverse(root)
    }

    return parentIds
}

interface CustomCommentBlockProps {
    node: FlowLayoutNode
    breakPoint?: BreakPointSize
    readonly?: boolean
    onBlockChange?: (values: BlockAbstract, origin: BlockAbstract) => void
    onUpdateRecord?: (recordId: string, content: RecordLikeProtocol['content']) => Promise<RecordLikeProtocol>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol) => string
}

export const CustomTreeBlock = memo(
    forwardRef<HTMLDivElement, CustomCommentBlockProps>((props, ref) => {
        const { node, readonly, onUpdateRecord, onRecordClick, onRenderRichText } = props
        const { blockData, recordPoints, recordPoolAtom, dataSource } = useViewBlockContext()
        const { id: dsId, type: dsType, schema, viewOptions } = dataSource
        const { id, title, config } = blockData
        const { breakPoint: baseBreakPointConfig, treeConfig, canDragTreeSort, anchor } = config as CustomViewConfig
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const { parentId, sortId } = treeConfig || {}
        const showEmpty = !dataSource || recordPoints.length === 0 || !parentId
        const { viewLayout, layout, tree: treeExpandCollapse, emptyView } = baseBreakPointConfig
        const gap = viewLayout?.gap?.size || 0
        const { indent, align = 'center', topMargin = 0, expandIcon, collapseIcon, defaultExpandAll = true } = treeExpandCollapse || {}

        // 检查当前block是否被选中
        const { selectedNodes } = useFlowLayoutContext()
        const isBlockSelected = useMemo(
            () => selectedNodes?.some(item => item.scope === node.scope && item.id === node.id),
            [selectedNodes, node.scope, node.id]
        )

        // 仅当block被选中且配置了sortId时启用拖拽
        const enableDrag = canDragTreeSort && (readonly ? true : isBlockSelected) && Boolean(sortId)

        // Drop信息状态，用于视觉反馈（拖拽时的缩进变化）
        const [overInfo, setOverInfo] = useState<{ overId: string; indent: number; newParentId: string } | null>(null)

        const idFieldId = useMemo(
            () => (dsType === DataSourceType.joinDataSource ? getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType) : 'ID') ?? 'ID',
            [dsType, schema, viewOptions]
        )

        const tree = useDeferredValue(
            useAtomData(
                recordPoolAtom,
                useCallback(
                    pool => {
                        if (!parentId) {
                            return []
                        }

                        const records = recordPoints
                            .map(p => pool.get(getRecordPoolKey(dsId, p.id)))
                            .filter((r): r is RecordLikeProtocol => r !== undefined)

                        return buildRecordTree(records, idFieldId, parentId, sortId)
                    },
                    [parentId, recordPoints, idFieldId, sortId, dsId]
                )
            )
        )

        const allParentNodeIds = useMemo(() => getAllParentNodeIds(tree), [tree])
        const equalAllParentNodeIds = useDeepCompareMemo(() => allParentNodeIds, [allParentNodeIds])

        // 管理折叠节点状态
        const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(() => {
            if (defaultExpandAll) {
                return new Set()
            }
            // 如果不是默认全部展开，则初始折叠所有父节点
            return new Set(equalAllParentNodeIds)
        })

        // 当树结构或defaultExpandAll变化时，重置折叠状态
        useEffect(() => {
            if (defaultExpandAll) {
                setCollapsedNodes(new Set())
            } else {
                setCollapsedNodes(new Set(equalAllParentNodeIds))
            }
        }, [defaultExpandAll, equalAllParentNodeIds])

        const toggleNode = useCallback((recordId: string) => {
            setCollapsedNodes(prev => {
                const next = new Set(prev)
                if (next.has(recordId)) {
                    next.delete(recordId)
                } else {
                    next.add(recordId)
                }
                return next
            })
        }, [])

        // 将树形结构扁平化为列表，包含层级信息，用于渲染和拖拽
        const flattenedNodes = useMemo(() => {
            const nodes: {
                id: string
                parentId: string
                level: number
                hasChildren: boolean
                isCollapsed: boolean
                record: RecordLikeProtocol
                index: number
            }[] = []

            const flatten = (treeNodes: TreeRecord[], currentParentId: string, currentLevel: number) => {
                for (const node of treeNodes) {
                    const hasChildren = node.children.length > 0
                    const isCollapsed = collapsedNodes.has(node.record.id)

                    nodes.push({
                        id: node.record.id,
                        parentId: currentParentId,
                        level: currentLevel,
                        hasChildren,
                        isCollapsed,
                        record: node.record,
                        index: node.index
                    })

                    if (hasChildren && !isCollapsed) {
                        flatten(node.children, node.record.id, currentLevel + 1)
                    }
                }
            }

            flatten(tree, '', 0)
            return nodes
        }, [tree, collapsedNodes])

        // 根据缩进层级获取父节点ID（参考PageLayers实现）
        const getParentId = useCallback(
            (indent: number, overIndex: number, prevItem?: typeof flattenedNodes[0]): string => {
                // 根节点或无前驱节点
                if (indent === 0 || !prevItem) {
                    return ''
                }

                // 相同层级：使用prevItem的父节点
                if (indent === prevItem.level) {
                    return prevItem.parentId
                }

                // 更深层级：成为prevItem的子节点
                if (indent > prevItem.level) {
                    return prevItem.id
                }

                // 更高层级：向前查找目标层级的节点，使用其父节点
                const ancestor = flattenedNodes
                    .slice(0, overIndex)
                    .reverse()
                    .find(item => item.level === indent)

                return ancestor?.parentId || ''
            },
            [flattenedNodes]
        )

        /**
         * ============================================================
         * 树形拖拽排序算法
         * ============================================================
         *
         * 设计模式：基于X轴的树形排序 + sortId字段排序
         *
         * 核心原则：
         * 1. Y轴（垂直拖动）→ 控制位置
         * 2. X轴（水平拖动）→ 控制缩进层级（父子关系）
         * 3. 排序值存储在数据源的 sortId 字段中
         * 4. 拖拽结束时计算新的 sortId 值并更新数据库
         *
         * sortId 值计算策略：
         * - 插入到两个节点之间：取 (prevSortId + nextSortId) / 2
         * - 插入到开头：取 firstSortId - STEP
         * - 插入到末尾：取 lastSortId + STEP
         */

        // 检查targetId是否是nodeId的后代节点（防止循环引用）
        const isDescendant = useCallback((nodeId: string, targetId: string, nodes: TreeRecord[]): boolean => {
            for (const node of nodes) {
                if (node.record.id === nodeId) {
                    const checkDescendants = (children: TreeRecord[]): boolean => {
                        for (const child of children) {
                            if (child.record.id === targetId) {
                                return true
                            }
                            if (child.children.length > 0 && checkDescendants(child.children)) {
                                return true
                            }
                        }
                        return false
                    }
                    return checkDescendants(node.children)
                }
                if (node.children.length > 0 && isDescendant(nodeId, targetId, node.children)) {
                    return true
                }
            }
            return false
        }, [])

        /**
         * 处理拖动移动事件，计算树节点位置和缩进层级
         *
         * 核心逻辑：
         * 1. X轴控制缩进层级（树形层级结构）
         * 2. Y轴控制位置（通过arrayMove的视觉顺序）
         * 3. prevItem/nextItem决定缩进约束，不是over节点
         *
         * 关键原则：
         * - max indent = prevItem.level + 1（可以成为前一个节点的子节点）
         * - min indent = nextItem.level（不能低于下一个节点的层级）
         * - newParentId由finalIndent和prevItem关系决定
         * - over节点仅用于视觉反馈，不参与数据计算
         *
         * 示例流程：
         * 树结构: A → B → C
         * 拖动C到A和B之间，向右移动（delta.x > 0）
         *
         * 步骤1: 计算目标缩进 = C.level + round(delta.x / 12)
         * 步骤2: arrayMove([A,B,C], 2, 1) → [A, C, B]，获取prevItem=A, nextItem=B
         * 步骤3: 计算约束 max = A.level + 1, min = B.level
         * 步骤4: finalIndent限制在min和max之间
         * 步骤5: 调用getParentId计算newParentId
         * 步骤6: 更新状态触发视觉反馈
         */
        const handleDragMove = useCallback(
            ({ delta, active, over }: DragMoveEvent) => {
                if (!over) {
                    setOverInfo(null)
                    return
                }

                const activeId = active.id as string
                const overId = over.id as string
                const activeIndex = flattenedNodes.findIndex(n => n.id === activeId)
                const overIndex = flattenedNodes.findIndex(n => n.id === overId)
                const activeItem = flattenedNodes[activeIndex]
                const overItem = flattenedNodes[overIndex]

                if (!activeItem || !overItem) {
                    return
                }

                // 防止将节点拖入到自己的后代节点中（会造成循环引用）
                if (isDescendant(activeId, overId, tree)) {
                    setOverInfo(null)
                    return
                }

                // ============================================================
                // 步骤1: 从X轴鼠标移动量计算目标缩进层级
                // ============================================================
                // X轴delta控制层级深度（indent）
                // 正向delta.x → 向右移动 → 增加indent（树中更深层级）
                // 负向delta.x → 向左移动 → 减少indent（树中更高层级）
                const indentChange = Math.round(delta.x / (indent || 12))
                const currentIndent = activeItem.level + indentChange

                // ============================================================
                // 步骤2: 从视觉重排序后获取相邻节点
                // ============================================================
                // arrayMove模拟拖拽后的视觉结果
                // prevItem和nextItem定义缩进约束范围
                const newItems = arrayMove(flattenedNodes, activeIndex, overIndex)
                const prevItem = newItems[overIndex - 1]
                const nextItem = newItems[overIndex + 1]

                // ============================================================
                // 步骤3: 计算缩进约束范围
                // ============================================================
                // max = 最右边界（prevItem存在时可成为其子节点，否则必须是根节点）
                // min = 最左边界（nextItem存在时至少与其同级，否则无约束）
                const max = prevItem ? prevItem.level + 1 : 0
                const min = nextItem ? nextItem.level : 0

                // ============================================================
                // 步骤4: 将缩进值限制在有效范围内
                // ============================================================
                const finalIndent = Math.max(min, Math.min(max, currentIndent))

                // ============================================================
                // 步骤5: 根据最终缩进层级计算父节点ID
                // ============================================================
                // 直接使用getParentId统一处理，避免重复逻辑
                const newParentId = getParentId(finalIndent, overIndex, prevItem)

                // ============================================================
                // 步骤6: 如有变化则更新状态
                // ============================================================
                // 如果位置和层级都没变，跳过更新（优化性能）
                if (activeIndex === overIndex && finalIndent === activeItem.level) {
                    setOverInfo(null)
                    return
                }

                // 存储结果，用于视觉反馈（drop指示器）和拖动结束处理
                setOverInfo({ overId, indent: finalIndent, newParentId })
            },
            [flattenedNodes, indent, tree, isDescendant, getParentId]
        )

        // 拖拽传感器配置
        const sensors = useSensors(
            // 桌面端：移动 8px 激活
            useSensor(MouseSensor, {
                activationConstraint: { distance: 8 }
            }),
            // 移动端：长按 250ms 激活，允许 5px 容差
            useSensor(TouchSensor, {
                activationConstraint: {
                    delay: 250,
                    tolerance: 5
                }
            })
        )

        /**
         * 处理拖动结束事件，更新 sortId 和 parentId 字段
         *
         * 核心逻辑：
         * 1. 计算新的 sortId 值（基于相邻节点的 sortId）
         * 2. 如果父节点变化，同时更新 parentId 字段
         * 3. 自动展开收起的父节点
         */
        const handleDragEnd = useCallback(
            (event: DragEndEvent) => {
                // 清理视觉反馈
                setOverInfo(null)

                const { active, over } = event

                // 验证drop目标和overInfo可用性
                if (!over || !overInfo || !sortId) {
                    return
                }

                const draggedId = active.id as string
                const overId = over.id as string

                const draggedNode = flattenedNodes.find(n => n.id === draggedId)
                const overNode = flattenedNodes.find(n => n.id === overId)

                if (!draggedNode || !overNode) {
                    return
                }

                // 提取handleDragMove中计算的drop信息
                const { newParentId, indent: dropIndent } = overInfo
                const oldParentId = draggedNode.parentId
                const isSameParent = oldParentId === newParentId

                // 如果拖到自己且层级未变，跳过（无操作）
                if (draggedId === overId && draggedNode.level === dropIndent) {
                    return
                }

                // 自动展开收起的父节点（UX优化）
                if (newParentId && collapsedNodes.has(newParentId)) {
                    setCollapsedNodes(prev => {
                        const next = new Set(prev)
                        next.delete(newParentId)
                        return next
                    })
                }

                // ============================================================
                // 计算新的排序值（只更新被拖动的1条记录）
                // ============================================================
                // 获取arrayMove所需的位置索引
                const activeIndex = flattenedNodes.findIndex(n => n.id === draggedId)
                const overIndex = flattenedNodes.findIndex(n => n.id === overId)

                // 模拟arrayMove后的结果，计算插入位置
                const sortedItems = arrayMove(
                    flattenedNodes.map(n => ({
                        ...n,
                        parentId: n.id === draggedId ? newParentId : n.parentId
                    })),
                    activeIndex,
                    overIndex
                )

                // 在新父节点下找到兄弟节点（排除被拖动的记录）
                const newSiblings = sortedItems
                    .filter(n => n.parentId === newParentId && n.id !== draggedId)
                    .map(n => ({ id: n.id, record: n.record }))

                // 计算在兄弟节点中的插入索引
                const draggedInSorted = sortedItems.findIndex(n => n.id === draggedId)
                let insertIndex = 0
                for (let i = 0; i < draggedInSorted; i++) {
                    if (sortedItems[i].parentId === newParentId && sortedItems[i].id !== draggedId) {
                        insertIndex++
                    }
                }

                // 计算新的排序值（使用有效排序值：sortId 或 创建时间）
                const newSortValue = calculateNewSortValue(newSiblings, insertIndex, sortId)

                // ============================================================
                // 构建更新内容（一次请求更新所有字段）
                // ============================================================
                const updateContent: RecordLikeProtocol['content'] = {
                    [sortId]: {
                        ...schema[sortId],
                        value: newSortValue
                    }
                }

                // 如果父节点变化，同时更新 parentId 字段
                if (!isSameParent && parentId) {
                    const newParentValue = newParentId
                        ? flattenedNodes.find(n => n.id === newParentId)?.record.content[idFieldId]?.value
                        : null
                    updateContent[parentId] = {
                        ...schema[parentId],
                        value: newParentValue
                    } as FieldADTValue
                }

                // 一次请求更新记录
                onUpdateRecord?.(draggedId, updateContent)
            },
            [overInfo, flattenedNodes, sortId, parentId, onUpdateRecord, idFieldId, schema, collapsedNodes]
        )

        const { level } = useContainerLayoutContext()
        const rootRef = useRef<HTMLDivElement>(null)
        const scrollParent = useStableScrollElement(rootRef)

        const contextValue: ContainerLayoutContextValue = useMemo(() => {
            return {
                parentId: id,
                level: level + 1,
                size: {
                    width: {
                        size: 'fill'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                layout: {
                    ...layout,
                    align: { ...layout?.align, direction: DIRECTION.horizontal }
                }
            }
        }, [id, level, layout])

        // 收集所有节点ID用于sortable context
        const allNodeIds = useMemo(() => flattenedNodes.map(n => n.id), [flattenedNodes])

        if (showEmpty) {
            return <ViewEmptyState id={effectiveAnchorId} config={emptyView} onRenderRichText={onRenderRichText} />
        }

        const renderTreeItems = (items = flattenedNodes) =>
            items.map((item, renderOrder) => {
                const showDropIndicator = overInfo?.overId === item.id
                const dropIndent = showDropIndicator && overInfo ? overInfo.indent : undefined

                return (
                    <TreeItem
                        key={item.id}
                        record={item.record}
                        index={item.index}
                        indent={indent}
                        align={align}
                        topMargin={topMargin}
                        expandIcon={expandIcon}
                        collapseIcon={collapseIcon}
                        gap={gap}
                        readonly={readonly}
                        node={node}
                        blockData={blockData}
                        title={title}
                        isCollapsed={item.isCollapsed}
                        onToggleNode={toggleNode}
                        onRecordClick={onRecordClick}
                        canDragTreeSort={enableDrag}
                        renderOrder={renderOrder}
                        scrollParent={scrollParent}
                        level={item.level}
                        hasChildren={item.hasChildren}
                        showDropIndicator={showDropIndicator}
                        dropIndent={dropIndent}
                    />
                )
            })

        return (
            <ContainerLayoutProvider value={contextValue}>
                <Flex id={effectiveAnchorId} direction="column" gap={gap} ref={mergeRefs([ref, rootRef])}>
                    {enableDrag ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
                            <SortableContext items={allNodeIds} strategy={verticalListSortingStrategy}>
                                <LayoutGroup id={id}>
                                    <AnimatePresence mode="popLayout">{renderTreeItems()}</AnimatePresence>
                                </LayoutGroup>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        renderTreeItems()
                    )}
                </Flex>
            </ContainerLayoutProvider>
        )
    })
)
